import React, { useState } from 'react';
import { FiPaperclip, FiCornerUpLeft, FiCornerUpRight} from 'react-icons/fi';
import { BsClock, BsCheck2, BsCheck2All, BsXCircle } from 'react-icons/bs';
import './chat.css';
import { showDialog, format, A, t } from '../../services/HelperService';
import { ErrorIndicator } from '../products/ProductConfiguratorShared';
import { ResendChatMessageDlg } from '../../dialogs/ResendChatMessageDlg';
import { ImagePreview } from '../images/ImagePreview';
import { looksLikeHtml, renderSafeHtml } from './ChatHtml';

const STATUS_META = {
    queued: { Icon: BsClock, size: 16, colorClass: "chat-message-status--queued", titleKey: "application_strings.views.chat.rightPane.chat.message.status.queued" },
    sent: { Icon: BsCheck2, size: 16, colorClass: "chat-message-status--sent", titleKey: "application_strings.views.chat.rightPane.chat.message.status.sent" },
    delivered: { Icon: BsCheck2All, size: 16, colorClass: "chat-message-status--delivered", titleKey: "application_strings.views.chat.rightPane.chat.message.status.delivered"},
    read: { Icon: BsCheck2All, size: 16, colorClass: "chat-message-status--read", titleKey: "application_strings.views.chat.rightPane.chat.message.status.read" },
    failed: { Icon: BsXCircle, size: 16, colorClass: "chat-message-status--failed", titleKey: "application_strings.views.chat.rightPane.chat.message.status.failed" },
    // WhatsApp does not allow for the deletion of messages via it's API, and, furthermore, WhatsApp does not allow contacts to delete messages with Business Accounts
    // Therefore, no need to implement the deleted case yet, but the status does exist in the back-end if ever required
};

const buildTextPreview = (msg) => {
    if (!msg) return '';
    if (msg.type === 'image') return t("application_strings.views.chat.rightPane.chat.message.previewTokens.image");
    if (msg.type === 'video') return t("application_strings.views.chat.rightPane.chat.message.previewTokens.video");
    if (msg.type === 'audio') return t("application_strings.views.chat.rightPane.chat.message.previewTokens.audio");
    if (msg.type === 'attachment') return msg.fileName ? t("application_strings.views.chat.rightPane.chat.message.previewTokens.attachment", { fileName: msg.fileName }) : t("application_strings.views.chat.rightPane.chat.message.previewTokens.attachmentNoName");
    const raw = (msg.text || '').replace(/\s+/g, ' ').trim();
    return raw.length > 140 ? (raw.slice(0, 140) + '…') : raw;
};

function StatusIndicator({ status, isOutbound, hasBeenResent }) {
    if (!isOutbound) return null;

    if (hasBeenResent) {
        return (
            <FiCornerUpRight
                size={16}
                className="chat-message-status-indicator"
                title={t("application_strings.views.chat.rightPane.chat.message.status.resentAlternate")}
                aria-label={t("application_strings.views.chat.rightPane.chat.message.status.resentAlternate")}
            />
        );
    }

    const meta = STATUS_META[status];
    if (!meta) return null;

    const { Icon, size, colorClass, titleKey } = meta;
    return (
        <Icon
            size={size}
            className={["chat-message-status-indicator", colorClass].join(" ")}
            title={t(titleKey)}
            aria-label={t(titleKey)}
        />
    );
}

function renderWithMentions(txt, knownSet, meNameLower) {
    if (!txt) return null;
    const parts = [];
    const rx = /(^|[\s])@([A-Za-z0-9][\w.'-]*(?:\s+[A-Za-z0-9][\w.'-]*)?)(?=$|[\s,:!?)}\]])/g;
    let last = 0;
    txt.replace(rx, (m, pre, name, off) => {
        parts.push(txt.slice(last, off));
        if (pre) parts.push(pre);
        const n = name.trim().toLowerCase();
        const isKnown = !!knownSet && knownSet.has(n);
        const isMe = meNameLower && n === meNameLower;
        parts.push(
            isKnown
                ? <span className={`mention-token${isMe ? ' me' : ''}`} key={`m${off}`}>@{name}</span>
                : `@${name}`
        );
        last = off + m.length;
    });
    parts.push(txt.slice(last));
    return parts;
}

const downloadBase64File = (base64Data, fileName, mimeType) => {
    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType || 'application/octet-stream' });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

// *** Memoized Chat Message Component ***
export const ChatMessage = React.memo(function ChatMessage({
    id,
    parentId,
    replyToId,
    replyTo,
    contentType,
    type,
    isHtml,
    text,
    imageUrl,
    videoUrl,
    audioUrl,
    documentData,
    fileName,
    mimeType,
    senderName,
    time,
    status,
    isOutbound,
    isInternal,
    hasBeenResent,
    isMentionHit,
    isMentionUnread,
    isUnread,
    onResend,
    alternateChannels = [],
    onAlternateChannelSend,
    onStartReply,
    onJumpToMessage,
    children = [],
    mentionNameSet,
    meNameLower,
    contactID,
    contactName
}) {
    // variants (root + children)
    const variants = React.useMemo(() => {
        const parseTime = (t) => (t ? new Date(t).getTime() : 0);
        const all = [{ id, parentId, replyToId, replyTo, contentType, type, isHtml, text, imageUrl, videoUrl, audioUrl, documentData, fileName, mimeType, senderName, time, status, isOutbound, isInternal, hasBeenResent, isMentionHit, isMentionUnread, alternateChannels }, ...children];
        return all.sort((a, b) => parseTime(a.time) - parseTime(b.time));
    }, [id, parentId, replyToId, replyTo, contentType, type, isHtml, text, imageUrl, videoUrl, audioUrl, documentData, fileName, mimeType, senderName, time, status, isOutbound, isInternal, hasBeenResent, isMentionHit, isMentionUnread, alternateChannels, children]);

    const [idx, setIdx] = React.useState(Math.max(variants.length - 1, 0));
    React.useEffect(() => { setIdx(Math.max(variants.length - 1, 0)); }, [variants.length]);
    const current = variants[idx];

    const [previewImage, setPreviewImage] = useState(null);
    const canJumpToReply = !!current.replyTo?.id;

    const rowClass = [
        "chat-message-row",
        current.isOutbound ? "chat-message-row-user" : "chat-message-row-assistant",
    ].join(" ");

    const bubbleClass = [
        "chat-message-bubble",
        current.isOutbound ? "chat-message-bubble-user" : "chat-message-bubble-assistant",
        isUnread && "chat-message-bubble-unread",
        current.isInternal && "chat-message-bubble-internal"
    ].filter(Boolean).join(" ")

    const timeClass = [
        "chat-message-time",
        current.isOutbound ? "chat-message-time-user" : "chat-message-time-assistant",
    ].join(" ");

    const hasVariants = variants.length > 1;
    const goPrev = () => setIdx((i) => (i > 0 ? i - 1 : i));
    const goNext = () => setIdx((i) => (i < variants.length - 1 ? i + 1 : i));

    const openResendDialog = async () => {
        const preview = (current.contentType === "Text" || current.contentType === "Template") ? (current.text ?? "") : "[image]";
        const result = await showDialog(<ResendChatMessageDlg preview={preview} channels={alternateChannels} />);
        if (result?.action === "resend") onResend?.(current.id);
        if (result?.action === "resend-alternate" && (current.contentType === "Text" || current.contentType === "Template")) onAlternateChannelSend?.(current.text, result.channel, current.id);
    };

    const mediaUrl = current.imageUrl || current.videoUrl || current.audioUrl || current.url || null;
    const isImage = current.type === 'image';
    const isVideo = current.type === 'video';
    const isAudio = current.type === 'audio';
    const isDoc = current.type === 'attachment';

    const handleDocumentDownload = () => {
        if (current.documentData && current.fileName) {
            downloadBase64File(current.documentData, current.fileName, current.mimeType);
        }
    };

    return (
        <>
            <div data-message-id={current.id} className={rowClass}>
                {current.isOutbound && (
                    <button
                        type="button"
                        className="chat-reply-icon"
                        onClick={() => onStartReply?.(current)}
                        title={t("application_strings.views.chat.rightPane.chat.message.actions.reply")}
                        aria-label={t("application_strings.views.chat.rightPane.chat.message.actions.reply")}
                    >
                        <FiCornerUpLeft size={16} />
                    </button>
                )}

                <div className="chat-message-stack">
                    <div className={bubbleClass}>
                        {!!current.replyTo && (
                            <div
                                className={`chat-reply-context ${canJumpToReply ? '' : 'chat-reply-context--disabled'}`}
                                onClick={canJumpToReply ? () => onJumpToMessage?.(current.replyTo.id) : undefined}
                                title={canJumpToReply ? t("application_strings.views.chat.rightPane.chat.message.actions.jumpToReply") : t("application_strings.views.chat.rightPane.chat.message.actions.replyOriginalMissing")}
                                role={canJumpToReply ? "button" : undefined}
                                tabIndex={canJumpToReply ? 0 : -1}
                            >
                                {current.replyTo.imageUrl ? <img className="chat-reply-thumb" src={current.replyTo.imageUrl} alt={t("application_strings.views.chat.rightPane.chat.message.replyContext.thumbAlt")} /> : null}
                                <div className="chat-reply-context-body">
                                    <div className="chat-reply-context-sender">{current.replyTo.senderName || t("application_strings.views.chat.rightPane.chat.message.replyContext.previousConversationSender")}</div>
                                    <div className="chat-reply-context-text">{current.replyTo.textPreview || t("application_strings.views.chat.rightPane.chat.message.replyContext.previousConversationText")}</div>
                                </div>
                            </div>
                        )}

                        {current.type === "text" && !!current.text && (
                            current.isHtml
                                ? <div className="chat-message-text chat-message-text-html">{renderSafeHtml(current.text)}</div>
                                : <p className="chat-message-text">{renderWithMentions(current.text, mentionNameSet, meNameLower)}</p>
                        )}

                        {isImage && (
                            mediaUrl ? (
                                <>
                                    <img
                                        src={mediaUrl}
                                        alt={t("application_strings.views.chat.rightPane.chat.message.media.imageAlt")}
                                        className="chat-message-image"
                                        onClick={() => setPreviewImage(mediaUrl)}
                                    />
                                    {current.text && <p className="chat-message-text chat-caption">{renderWithMentions(current.text, mentionNameSet, meNameLower)}</p>}
                                </>
                            ) : (
                                <div className="chat-attachment-bubble" aria-disabled="true">
                                        <FiPaperclip size={16} /><span className="chat-attachment-label">{t("application_strings.views.chat.rightPane.chat.message.media.imageSending")}</span>
                                </div>
                            )
                        )}

                        {/* VIDEO */}
                        {isVideo && (
                            mediaUrl ? (
                                <>
                                    <video className="chat-message-video" controls src={mediaUrl} />
                                    {current.text && <p className="chat-message-text chat-caption">{renderWithMentions(current.text, mentionNameSet, meNameLower)}</p>}
                                </>
                            ) : (
                                <div className="chat-attachment-bubble" aria-disabled="true">
                                        <FiPaperclip size={16} /><span className="chat-attachment-label">{t("application_strings.views.chat.rightPane.chat.message.media.videoSending")}</span>
                                </div>
                            )
                        )}

                        {/* AUDIO */}
                        {isAudio && (
                            mediaUrl ? (
                                <>
                                    <audio className="chat-message-audio" controls src={mediaUrl} />
                                    {current.text && <p className="chat-message-text chat-caption">{renderWithMentions(current.text, mentionNameSet, meNameLower)}</p>}
                                </>
                            ) : (
                                <div className="chat-attachment-bubble" aria-disabled="true">
                                        <FiPaperclip size={16} /><span className="chat-attachment-label">{t("application_strings.views.chat.rightPane.chat.message.media.audioSending")}</span>
                                </div>
                            )
                        )}

                        {/* DOCUMENT */}
                        {isDoc && (
                            <>
                                <div
                                    className={`chat-attachment-bubble ${current.documentData ? 'is-clickable' : 'is-disabled'}`}
                                    onClick={current.documentData ? handleDocumentDownload : undefined}
                                    disabled={!current.documentData}
                                >
                                    <FiPaperclip size={16} />
                                    <span className="chat-attachment-label">
                                        {current.fileName || t("application_strings.views.chat.rightPane.chat.message.media.documentLabelFallback")} {/* <-- remove caption from label */}
                                    </span>
                                </div>
                                {current.text && <p className="chat-message-text chat-caption">{current.text}</p>}
                            </>
                        )}

                        <p className={timeClass}>
                            {/* Link to the contact for inbound messages only */}
                            {(!current.isOutbound && contactID) ? (
                                <>
                                    <A
                                        className={`acc-link ${current.isOutbound ? 'acc-link--inverse' : ''}`}
                                        href={`/Contact/${contactID}`}
                                        title={t("application_strings.views.chat.rightPane.chat.message.footer.openContactTitle")}
                                    >
                                        {current.senderName || contactName || t("application_strings.views.chat.rightPane.chat.message.footer.contactFallback")}
                                    </A>
                                    {t("application_strings.views.chat.rightPane.chat.message.footer.timeSeparator")}
                                </>
                            ) : (
                                <>
                                    {current.senderName}
                                        {t("application_strings.views.chat.rightPane.chat.message.footer.timeSeparator")}
                                </>
                            )}
                            {format.formatDateTime(current.time)}
                            {current.isInternal
                                ? <span className="chat-message-internal-badge" title={t("application_strings.views.chat.rightPane.chat.message.footer.internalBadgeTitle")}>{t("application_strings.views.chat.rightPane.chat.message.footer.internalBadge")}</span>
                                : <StatusIndicator status={current.status} isOutbound={current.isOutbound} hasBeenResent={current.hasBeenResent} />
                            }
                        </p>
                    </div>

                    {hasVariants && (
                        <div className="chat-variant-selector">
                            <button
                                type="button"
                                className="chat-variant-button"
                                onClick={goPrev}
                                disabled={idx === 0}
                                aria-label={t("application_strings.views.chat.rightPane.chat.message.actions.previousAttempt")}
                                title={t("application_strings.views.chat.rightPane.chat.message.actions.previousAttempt")}
                            >
                                &lt;
                            </button>
                            <span className="chat-variant-index" aria-live="polite">{`${idx + 1}/${variants.length}`}</span>
                            <button
                                type="button"
                                className="chat-variant-button"
                                onClick={goNext}
                                disabled={idx === variants.length - 1}
                                aria-label={t("application_strings.views.chat.rightPane.chat.message.actions.nextAttempt")}
                                title={t("application_strings.views.chat.rightPane.chat.message.actions.nextAttempt")}
                            >
                                &gt;
                            </button>
                        </div>
                    )}
                </div>

                {!current.isOutbound && (
                    <button
                        type="button"
                        className="chat-reply-icon"
                        onClick={() => onStartReply?.(current)}
                        title={t("application_strings.views.chat.rightPane.chat.message.actions.reply")}
                    >
                        <FiCornerUpLeft size={16} />
                    </button>
                )}

                {variants[variants.length - 1].status === "failed" && !variants[variants.length - 1].hasBeenResent && (
                    <div
                        className="chat-error-indicator-click"
                        onClick={() => openResendDialog()}
                        role="button"
                        tabIndex={0}
                    >
                        <ErrorIndicator error={t("application_strings.views.chat.rightPane.chat.message.errors.failedToSend")} />
                    </div>
                )}
            </div>

            {previewImage && (
                <ImagePreview
                    imageUrl={previewImage}
                    onClose={() => setPreviewImage(null)}
                />
            )}
        </>
    );
});
// *** End of ChatMessage Component ***

export const makeReplyPreview = (m) => m ? ({
    id: m.id, senderName: m.senderName, textPreview: buildTextPreview(m),
    type: m.type, imageUrl: m.imageUrl, fileName: m.fileName
}) : null;

export const mapChatMessages = (messagesResult) => {
    const raw = (messagesResult || []).map(m => {
        const content = m.Content ?? m.content;
        const caption = m.Caption ?? m.caption;
        const ct = m.ContentType ?? m.contentType;
        const mimeType = m.MimeType ?? m.mimeType;

        const isDataUrl = typeof content === 'string' && content.startsWith('data:');
        const isImage = (isDataUrl && content.startsWith('data:image/')) || ct === 'Image';
        const isVideo = (isDataUrl && content.startsWith('data:video/')) || ct === 'Video';
        const isAudio = (isDataUrl && content.startsWith('data:audio/')) || ct === 'Audio';
        const isDocument = ct === 'Document';
        const isText = !isImage && !isVideo && !isAudio && !isDocument;
        const isHtml = looksLikeHtml(content);

        return {
            id: m.ID ?? m.id,
            parentId: m.ParentID ?? m.parentID ?? null,
            replyToId: m.ReplyToID ?? m.replyToID ?? null,
            replyToSenderName: m.ReplyToSender ?? m.replyToSender ?? null,
            replyToPreviewText: m.ReplyToPreview ?? m.replyToPreview ?? null,
            senderName: m.Sender ?? m.senderName,
            isOutbound: m.IsOutbound ?? m.isOutbound ?? false,
            isInternal: m.IsInternal ?? m.isInternal ?? false,
            hasBeenResent: m.HasBeenResent ?? m.hasBeenResent ?? false,
            isMentionHit: !!(m.IsMentionHit ?? m.isMentionHit),
            isMentionUnread: !!(m.IsMentionUnread ?? m.isMentionUnread),
            time: m.Time ?? m.time,
            status: m.Status ?? m.status,
            channel: m.Channel ?? m.channel,
            contentType: ct,
            text: isText ? content : (caption || null),
            imageUrl: isImage ? content : null,
            videoUrl: isVideo ? content : null,
            audioUrl: isAudio ? content : null,
            documentData: isDocument ? content : null,
            fileName: m.FileName ?? m.fileName,
            mimeType,
            type: isImage ? 'image' : isVideo ? 'video' : isAudio ? 'audio' : isDocument ? 'attachment' : 'text',
            isHtml: isHtml || ct === 'Template'
        };
    });

    // index by id and attach reply previews
    const byId = new Map(raw.map(r => [String(r.id), r]));
    raw.forEach(msg => {
        if (msg.replyToId) {
            const target = byId.get(String(msg.replyToId));
            if (target) {
                msg.replyTo = makeReplyPreview(target);
            } else {
            // Use server-supplied sender/preview when cross-chat
                msg.replyTo = {
                    id: null,
                    senderName: msg.replyToSenderName || t("application_strings.views.chat.rightPane.chat.message.replyContext.previousConversationSender"),
                    textPreview: msg.replyToPreviewText || t("application_strings.views.chat.rightPane.chat.message.replyContext.previousConversationText")
                };
            }
        }
    });

    // Prepare root buckets
    const roots = new Map(); // id -> { ...root, children: [] }

    // First add roots
    raw.forEach(msg => {
        if (!msg.parentId) {
            roots.set(String(msg.id), { ...msg, children: [] });
        }
    });

    // Attach children to their roots (skip orphans gracefully)
    raw.forEach(msg => {
        if (msg.parentId) {
            const rootId = findRootId(msg, byId);
            const root = rootId ? roots.get(rootId) : null;
            if (root) {
                root.children.push(msg);
            } else {
                // If parent isn't in payload yet, treat this as its own root
                roots.set(String(msg.id), { ...msg, children: [] });
            }
        }
    });

    // For each root, sort variants and set root display defaults to newest
    const rootsArray = Array.from(roots.values()).map(root => {
        const sortedChildren = [...root.children]
            .filter(c => c.id !== root.id) // never include root as its own child
            .sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));
        return { ...root, children: sortedChildren };
    });

    return rootsArray;
};

// Walk up to the true root id (handles multi-level chains defensively)
function findRootId(msg, byId) {
    let current = msg;
    const guard = new Set();
    while (current && current.parentId && !guard.has(String(current.id))) {
        guard.add(String(current.id));
        const parent = byId.get(String(current.parentId));
        if (!parent) return null;
        current = parent;
    }
    return current ? String(current.id) : null;
}